import React from 'react';
import { 
  FaGem, FaHeart, FaAward, FaUsers, FaShieldAlt, FaStar, 
  FaRing, FaClock, FaCheckCircle, FaArrowRight,
  FaCrown, FaHandHoldingHeart, FaCertificate, FaTrophy, FaMapMarkerAlt,
  FaInfinity, FaDiamond
} from 'react-icons/fa';
import { MdSecurity, MdVerified } from 'react-icons/md';

const About = () => {
  return (
    <section className="w-full bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/40 py-10 sm:py-12 md:py-16 lg:py-20 overflow-hidden relative">
      {/* Decorative Background Elements - Adjusted for mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-60 h-60 sm:w-80 sm:h-80 bg-amber-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-60 h-60 sm:w-80 sm:h-80 bg-orange-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] lg:w-[600px] lg:h-[600px] bg-gradient-to-r from-amber-100/10 to-orange-100/10 rounded-full blur-2xl"></div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Content - Responsive grid */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Enhanced Text Content */}
          <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8 order-2 lg:order-1">
            {/* Main Description */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg sm:rounded-xl shadow-lg">
                  <FaCrown className="text-white text-base sm:text-lg md:text-xl" />
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                  Swarn Kala Kendra
                </h3>
              </div>
              
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base md:text-lg">
                For <span className="font-extrabold text-amber-600 text-base sm:text-lg md:text-xl">25+ years</span>,{' '}
                <span className="font-bold text-gray-800">Swarn Kala Kendra</span> has been Ballia's most trusted jeweller. 
                We offer exquisite <span className="font-semibold text-amber-600">gold jewellery, diamond sets, bridal collections</span> that blend 
                traditional artistry with contemporary designs.
              </p>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border-l-4 border-amber-500 shadow-sm">
                <p className="text-gray-700 italic text-xs sm:text-sm md:text-base leading-relaxed">
                  "Every piece at Swarn Kala Kendra is <span className="font-bold text-amber-600">BIS hallmarked & certified</span> 
                  for purity and quality. Our commitment to transparency and customer satisfaction 
                  has made us the preferred choice for generations."
                </p>
              </div>
            </div>
            
            {/* Mission Statement - Enhanced */}
            <div className="relative bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg sm:rounded-xl p-4 sm:p-5 shadow-md">
              <div className="absolute -top-2 sm:-top-3 -left-2 sm:-left-3">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full p-1 sm:p-1.5 shadow-lg">
                  <FaHandHoldingHeart className="text-white text-[10px] sm:text-xs" />
                </div>
              </div>
              <p className="text-gray-800 italic text-xs sm:text-sm md:text-base leading-relaxed pl-3 sm:pl-4">
                "We believe jewellery is not just an ornament — it's an emotion, a celebration, and a legacy to cherish forever."
              </p>
              <div className="mt-2 sm:mt-3 flex items-center justify-end gap-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400"></div>
                <p className="text-amber-700 font-bold text-[10px] sm:text-xs md:text-sm">- Swarn Kala Kendra Team</p>
              </div>
            </div>

            {/* CTA Button - Enhanced with animation */}
            <button className="group relative bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 sm:gap-3 w-fit text-sm sm:text-base md:text-lg overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-orange-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <FaMapMarkerAlt className="relative z-10 text-xs sm:text-sm md:text-base group-hover:scale-110 transition-transform duration-300" />
              <span className="relative z-10">Visit Our Showroom</span>
              <FaArrowRight className="relative z-10 text-xs sm:text-sm md:text-base group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>

          {/* Right Side - Enhanced Image Section */}
          <div className="relative group order-1 lg:order-2 mb-6 lg:mb-0">
            {/* Main Image Container with Glow Effect */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl transform group-hover:scale-[1.01] sm:group-hover:scale-[1.02] transition-all duration-500">
              {/* Animated Border Gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl sm:rounded-2xl blur-md sm:blur-xl"></div>
              
              {/* Image with Overlay */}
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10"></div>
                
                <img 
                  src="image1.png" 
                  alt="Swarn Kala Kendra Jewellers Showroom"
                  className="w-full h-[250px] sm:h-[350px] md:h-[400px] lg:h-[500px] object-cover transform group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-700"
                />
                
                {/* Overlay Content - Responsive padding */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 md:p-6 lg:p-8 z-20">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-white mb-1 sm:mb-2">
                    <FaCrown className="text-yellow-400 text-base sm:text-lg md:text-xl lg:text-2xl animate-pulse" />
                    <span className="text-[9px] sm:text-xs md:text-sm font-semibold tracking-wider bg-white/20 backdrop-blur-sm px-1.5 sm:px-2 py-0.5 rounded-full">
                      PREMIUM JEWELLERY STORE
                    </span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-bold">Swarn Kala Kendra</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs md:text-sm lg:text-base mt-0.5 sm:mt-1 flex items-center gap-1">
                    <FaMapMarkerAlt className="text-amber-400 text-[8px] sm:text-xs" />
                    Arya Samaj Road, Okdenganj, Ballia
                  </p>
                </div>
              </div>
            </div>

            {/* Floating Elements - Responsive positioning */}
            <div className="absolute -top-2 sm:-top-3 md:-top-4 -right-2 sm:-right-3 md:-right-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-1.5 sm:p-2 shadow-xl z-30 animate-bounce-slow">
              <FaCertificate className="text-white text-xs sm:text-sm md:text-base lg:text-lg" />
            </div>
            
            <div className="absolute -bottom-2 sm:-bottom-3 md:-bottom-4 -left-2 sm:-left-3 md:-left-4 bg-white rounded-full p-1.5 sm:p-2 shadow-xl z-30 animate-pulse-slow">
              <MdVerified className="text-green-600 text-xs sm:text-sm md:text-base lg:text-lg" />
            </div>
            
            {/* Rating Badges - Responsive */}
            <div className="absolute top-2 sm:top-3 md:top-4 left-2 sm:left-3 md:left-4 bg-white/95 backdrop-blur-sm rounded-md sm:rounded-lg px-1.5 sm:px-2 md:px-2.5 py-1 sm:py-1.5 shadow-lg z-30">
              <div className="flex items-center gap-1 sm:gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-500 text-[8px] sm:text-xs" />
                ))}
                <span className="text-[8px] sm:text-xs font-bold ml-0.5 sm:ml-1">4.9</span>
              </div>
            </div>
            
            <div className="absolute bottom-16 sm:bottom-20 md:bottom-24 right-2 sm:right-3 md:right-4 bg-white/95 backdrop-blur-sm rounded-md sm:rounded-lg px-1.5 sm:px-2 md:px-2.5 py-1 sm:py-1.5 shadow-lg z-30">
              <div className="flex items-center gap-1 sm:gap-1.5">
                <FaUsers className="text-amber-500 text-[8px] sm:text-xs" />
                <span className="text-[8px] sm:text-xs font-bold">10K+ Happy Customers</span>
              </div>
            </div>

            {/* Decorative Ring */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100%] h-[100%] rounded-full bg-gradient-to-r from-amber-300/20 to-orange-300/20 blur-xl sm:blur-2xl"></div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        /* Better touch targets for mobile */
        @media (max-width: 640px) {
          button, a {
            min-height: 44px;
          }
        }
      `}</style>
    </section>
  );
};

export default About;